"use client";

import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth-client";

import { Button, Input, Label } from "@meetploy/storybook";

interface LoginFormValues {
	email: string;
	password: string;
}

function safeReturnUrl(url: string | null): string {
	if (url?.startsWith("/") && !url.startsWith("//")) {
		return url;
	}
	return "/";
}

function LoginForm() {
	const router = useRouter();
	const queryClient = useQueryClient();
	const searchParams = useSearchParams();
	const { signIn } = useAuth();
	const [isLoading, setIsLoading] = useState(false);

	const returnUrl = safeReturnUrl(searchParams.get("returnUrl"));

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormValues>({
		defaultValues: { email: "", password: "" },
	});

	const onSubmit = handleSubmit(async (values) => {
		setIsLoading(true);

		const { error } = await signIn.email(
			{ email: values.email, password: values.password },
			{
				onSuccess: () => {
					queryClient.clear();
					router.push(returnUrl);
				},
			},
		);

		if (error) {
			toast.error(error.message ?? "An unknown error occurred");
		}

		setIsLoading(false);
	});

	return (
		<div className="mx-auto flex h-screen w-full max-w-sm flex-col justify-center gap-6 px-4">
			<div className="flex flex-col gap-2 text-center">
				<h1 className="text-2xl font-semibold tracking-tight">Ploy Admin</h1>
				<p className="text-muted-foreground text-sm">
					Sign in with an instance admin account
				</p>
			</div>

			<form onSubmit={onSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="email">Email</Label>
					<Input
						id="email"
						type="email"
						autoComplete="username"
						placeholder="name@example.com"
						{...register("email", { required: "Email is required" })}
					/>
					{errors.email ? (
						<p className="text-destructive text-xs">{errors.email.message}</p>
					) : null}
				</div>

				<div className="flex flex-col gap-2">
					<Label htmlFor="password">Password</Label>
					<Input
						id="password"
						type="password"
						autoComplete="current-password"
						placeholder="••••••••"
						{...register("password", { required: "Password is required" })}
					/>
					{errors.password ? (
						<p className="text-destructive text-xs">
							{errors.password.message}
						</p>
					) : null}
				</div>

				<Button type="submit" className="w-full" disabled={isLoading}>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Signing in...
						</>
					) : (
						"Sign in"
					)}
				</Button>
			</form>
		</div>
	);
}

export default function LoginPage() {
	return (
		<Suspense>
			<LoginForm />
		</Suspense>
	);
}
